import React from 'react';
import {
  Box,
  Grid,
  GridItem,
  Flex,
  Skeleton,
  useMediaQuery,
  Text,
  Divider,
} from '@chakra-ui/react';
import DonutChart from './DonutChart';
import LineChart from './LineChart';
import { useContext } from 'react';
import { ClientDashboardStatsContext } from '../../../store/client/ClientDashboardStatsContext';
import TimeRangeSelector from './TimeRangeSelector';
import NoDataBox from './NoDataBox';

export default function PassesStats({ getData }) {
  const [isLargerThanMd] = useMediaQuery('(min-width: 768px)');
  const dashboardCtx = useContext(ClientDashboardStatsContext);
  const { dashboardState, updateSectionTimeRange, loadingStates } =
    dashboardCtx;
  const timeRange = dashboardState.passStats.timeRange;
  const isLoading = loadingStates.pass || loadingStates.global;
  const hasTemplates = dashboardState.totalPassTemplates > 0;
  const hasPassesInRange = dashboardState.passStats.totalPasses > 0;

  const PASS_STATUS_COLORS = {
    issued: '#8E9BA3',
    active: '#ec685a',
    deactivated: '#2f3941',
    removed: '#fbafa7',
  };

  const PASS_DEVICE_COLORS = {
    apple: '#2f3941',
    google: '#ec685a',
  };

  const formatDonutDataStatus = (passesByStatus) => {
    return Object.entries(passesByStatus).map(([status, value]) => ({
      id: status.charAt(0).toUpperCase() + status.slice(1),
      label: status.charAt(0).toUpperCase() + status.slice(1),
      value,
      color: PASS_STATUS_COLORS[status],
    }));
  };

  const formatDonutDataDevice = (passesByDevice) => {
    return Object.entries(passesByDevice).map(([device, value]) => ({
      id: device.charAt(0).toUpperCase() + device.slice(1),
      label: device.charAt(0).toUpperCase() + device.slice(1),
      value,
      color: PASS_DEVICE_COLORS[device],
    }));
  };

  const handleRangeChange = (newRange) => {
    updateSectionTimeRange('pass', newRange);
    getData('pass', newRange);
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
      p={7}
      maxW="100%"
      w="100%"
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={4}
        flexDir={{ base: 'column', md: 'row' }}
        gap={3}
        w="100%"
      >
        <Text textStyle="heading4">Passes</Text>
        {hasTemplates && (
          <TimeRangeSelector
            selectedRange={timeRange}
            onRangeChange={handleRangeChange}
          />
        )}
      </Flex>
      <Divider mb={4} border="1px solid #D9D9D9" />
      {!hasPassesInRange && !isLoading ? (
        hasTemplates ? (
          <NoDataBox
            title="No data"
            description="No passes were issued in this date range. Try selecting a different range or check back later."
          />
        ) : (
          <NoDataBox
            title="No passes yet"
            description="Any passes you create during your trial are for testing only. All trial passes will expire when your trial ends — go live to keep them active."
            buttonText="Issue pass"
            buttonLink="/passes"
            buttonType="outline"
            fullWidth={true}
          />
        )
      ) : (
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            xl: 'repeat(4, 1fr)',
          }}
          gap={8}
          mt={10}
          mb={6}
        >
          <Skeleton isLoaded={!isLoading} fadeDuration={1}>
            <Box height="300px">
              <DonutChart
                data={formatDonutDataStatus(
                  dashboardState.passStats.passesByStatus
                )}
                label="Total Passes"
              />
            </Box>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} fadeDuration={1}>
            <Box height="300px">
              <DonutChart
                data={formatDonutDataDevice(
                  dashboardState.passStats.passesByDevice
                )}
                label="Active Passes"
              />
            </Box>
          </Skeleton>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <Skeleton isLoaded={!isLoading} fadeDuration={1}>
              <Box height="300px">
                <LineChart
                  data={dashboardState.passStats.issuedOverTime}
                  label="Passes Issued"
                  timeRange={timeRange}
                  tickRotation={isLargerThanMd ? 0 : 45}
                />
              </Box>
            </Skeleton>
          </GridItem>
        </Grid>
      )}
    </Box>
  );
}
